/**
 * API-Specific Hooks
 *
 * Higher-level hooks that wrap useFetch with specific API endpoint logic,
 * providing excellent developer experience with proper typing and error handling.
 */

import type {
	HealthResponse,
	PageViewRequest,
	PageViewResponse,
	RecentActivityResponse,
	SdrCreateRequest,
	SdrCreateResponse,
	SdrGetResponse,
	SdrLeadCreateResponse,
	SdrLeadsListResponse,
	SdrListResponse,
	SdrPerformanceResponse,
	UserListResponse,
	UserStatsResponse,
	WaitlistJoinResponse,
	WaitlistStatusResponse,
} from "@shared/types";
import { useCallback } from "react";
import { useGet, useMutation } from "./useFetch";

// ===== SDR API Hooks =====

/**
 * Get list of user's SDRs
 */
export function useSDRList() {
	return useGet<SdrListResponse>("/api/sdr", {
		showErrorToast: true,
		staleTime: 30000, // 30 seconds
	});
}

/**
 * Get specific SDR details
 */
export function useSDR(sdrId: string | null) {
	return useGet<SdrGetResponse>(sdrId ? `/api/sdr/${sdrId}` : null, {
		showErrorToast: true,
		staleTime: 60000, // 1 minute
	});
}

/**
 * Create new SDR
 */
export function useCreateSDR() {
	return useMutation<SdrCreateRequest, SdrCreateResponse>(
		"/api/sdr/create",
		"POST",
		{
			showSuccessToast: true,
			successMessage: "SDR created successfully!",
			showErrorToast: true,
		},
	);
}

/**
 * Get the last created SDR for the current user
 */
export function useLastCreatedSDR() {
	return useGet<SdrListResponse>("/api/sdr", {
		showErrorToast: true,
		staleTime: 30000,
		parseResponse: async (response) => {
			const data = await response.json();
			// Return only the most recent SDR (sorted by createdAt desc)
			if (data.sdrs && data.sdrs.length > 0) {
				const sortedSDRs = data.sdrs.sort(
					(a: any, b: any) =>
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
				);
				return { sdrs: [sortedSDRs[0]], count: 1 };
			}
			return { sdrs: [], count: 0 };
		},
	});
}

/**
 * Get leads for specific SDR
 */
export function useSDRLeads(sdrId: string | null) {
	return useGet<SdrLeadsListResponse>(
		sdrId ? `/api/sdr/${sdrId}/leads` : null,
		{
			showErrorToast: true,
			staleTime: 30000,
		},
	);
}

/**
 * Get the last created lead for a specific SDR
 */
export function useLastCreatedLead(sdrId: string | null) {
	return useGet<SdrLeadsListResponse>(
		sdrId ? `/api/sdr/${sdrId}/leads` : null,
		{
			showErrorToast: true,
			staleTime: 30000,
			parseResponse: async (response) => {
				const data = await response.json();
				// Return only the most recent lead (sorted by createdAt desc)
				if (data.leads && data.leads.length > 0) {
					const sortedLeads = data.leads.sort(
						(a: any, b: any) =>
							new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
					);
					return { sdrId: data.sdrId, leads: [sortedLeads[0]], count: 1 };
				}
				return { sdrId: data.sdrId || sdrId, leads: [], count: 0 };
			},
		},
	);
}

/**
 * Create new lead for SDR
 */
export function useCreateSDRLead(sdrId: string) {
	return useMutation<{}, SdrLeadCreateResponse>(
		`/api/sdr/${sdrId}/lead`,
		"POST",
		{
			showSuccessToast: true,
			successMessage: "Lead conversation started!",
			showErrorToast: true,
		},
	);
}

// ===== Waitlist API Hooks =====

/**
 * Join waitlist
 */
export function useJoinWaitlist() {
	return useMutation<{}, WaitlistJoinResponse>("/api/waitlist/join", "POST", {
		showSuccessToast: true,
		successMessage: "Successfully joined the waitlist!",
		showErrorToast: true,
	});
}

/**
 * Leave waitlist
 */
export function useLeaveWaitlist() {
	return useMutation<{}, WaitlistJoinResponse>("/api/waitlist/leave", "POST", {
		showSuccessToast: true,
		successMessage: "Left the waitlist",
		showErrorToast: true,
	});
}

/**
 * Get waitlist status
 */
export function useWaitlistStatus() {
	return useGet<WaitlistStatusResponse>("/api/waitlist/status", {
		showErrorToast: true,
		staleTime: 60000,
	});
}

// ===== Analytics API Hooks =====

/**
 * Get user statistics (admin only)
 */
export function useUserStats() {
	return useGet<UserStatsResponse>("/api/analytics/users", {
		showErrorToast: true,
		staleTime: 300000, // 5 minutes
	});
}

/**
 * Get SDR performance analytics (admin only)
 */
export function useSDRPerformance() {
	return useGet<SdrPerformanceResponse>("/api/analytics/sdr-performance", {
		showErrorToast: true,
		staleTime: 300000,
	});
}

/**
 * Get user list with analytics (admin only)
 */
export function useUserList() {
	const result = useGet<UserListResponse>("/api/analytics/user-list", {
		showErrorToast: true,
		staleTime: 300000,
	});

	// Transform the data to convert createdAt from ISO string to Date
	const transformedResult = {
		...result,
		data: result.data ? {
			...result.data,
			users: result.data.users?.map(user => ({
				...user,
				createdAt: new Date(user.createdAt)
			}))
		} : undefined
	};

	return transformedResult;
}

/**
 * Get recent activity feed (admin only)
 */
export function useRecentActivity() {
	return useGet<RecentActivityResponse>("/api/analytics/recent-activity", {
		showErrorToast: true,
		staleTime: 60000,
	});
}

/**
 * Track page view
 */
export function useTrackPageView() {
	return useMutation<PageViewRequest, PageViewResponse>(
		"/api/analytics/page-view",
		"POST",
		{
			showSuccessToast: false,
			showErrorToast: false, // Don't show errors for analytics
		},
	);
}

// ===== Health API Hooks =====

/**
 * Health check
 */
export function useHealthCheck() {
	return useGet<HealthResponse>("/api/health", {
		showErrorToast: false,
		staleTime: 30000,
	});
}

// ===== Composite Hooks =====

/**
 * Complete SDR management hook with all operations
 */
export function useSDRManagement() {
	const sdrList = useSDRList();
	const createSDR = useCreateSDR();

	const createSDRWithRefresh = useCallback(
		async (persona: string) => {
			const result = await createSDR.mutate({ persona });
			if (result) {
				await sdrList.refetch();
			}
			return result;
		},
		[createSDR, sdrList],
	);

	return {
		list: sdrList,
		create: createSDRWithRefresh,
		creating: createSDR.loading,
		createError: createSDR.error,
	};
}

/**
 * Complete waitlist management hook
 */
export function useWaitlistManagement() {
	const status = useWaitlistStatus();
	const join = useJoinWaitlist();
	const leave = useLeaveWaitlist();

	const joinWithRefresh = useCallback(async () => {
		const result = await join.mutate({});
		if (result) {
			await status.refetch();
		}
		return result;
	}, [join, status]);

	const leaveWithRefresh = useCallback(async () => {
		const result = await leave.mutate({});
		if (result) {
			await status.refetch();
		}
		return result;
	}, [leave, status]);

	return {
		status: status.data?.waitlist || false,
		loading: status.loading,
		joining: join.loading,
		leaving: leave.loading,
		join: joinWithRefresh,
		leave: leaveWithRefresh,
		error: status.error || join.error || leave.error,
		refetch: status.refetch,
	};
}

/**
 * Complete analytics dashboard hook
 */
export function useAnalyticsDashboard() {
	const userStats = useUserStats();
	const sdrPerformance = useSDRPerformance();
	const userList = useUserList();
	const recentActivity = useRecentActivity();

	const refetchAll = useCallback(async () => {
		await Promise.all([
			userStats.refetch(),
			sdrPerformance.refetch(),
			userList.refetch(),
			recentActivity.refetch(),
		]);
	}, [userStats, sdrPerformance, userList, recentActivity]);

	return {
		userStats: userStats.data,
		sdrPerformance: sdrPerformance.data,
		userList: userList.data,
		recentActivity: recentActivity.data,
		loading:
			userStats.loading ||
			sdrPerformance.loading ||
			userList.loading ||
			recentActivity.loading,
		error:
			userStats.error ||
			sdrPerformance.error ||
			userList.error ||
			recentActivity.error,
		refetchAll,
	};
}

// ===== Page Tracking Hook =====

/**
 * Automatic page tracking hook
 */
export function usePageTracking(page: string, sessionId?: string) {
	const trackPageView = useTrackPageView();

	const track = useCallback(() => {
		trackPageView.mutate({
			page,
			sessionId: sessionId || crypto.randomUUID(),
			timestamp: Date.now(),
		});
	}, [trackPageView, page, sessionId]);

	return { track };
}

/**
 * Get list of user's SDRs (admin can specify userId)
 */
export function useSDRListForUser(userId?: string) {
	const url = userId ? `/api/sdr?userId=${userId}` : "/api/sdr";
	return useGet<SdrListResponse>(url, {
		showErrorToast: true,
		staleTime: 30000, // 30 seconds
	});
}

/**
 * Get leads for a specific SDR (admin can access any SDR)
 */
export function useSDRLeadsForAdmin(sdrId: string | null) {
	return useGet<SdrLeadsListResponse>(
		sdrId ? `/api/sdr/${sdrId}/leads` : null,
		{
			showErrorToast: true,
			staleTime: 30000,
		},
	);
}
